import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

// ─── Single-Month Range Selector ─────────────────────────────────────────────
// Shows one month at a time (fits inside side-sheet without horizontal scroll).
// Red endpoints (brand primary), light red range (red-50).

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = getDaysInMonth(year, month);
  const grid = [];
  for (let i = 0; i < firstDay; i++) grid.push(null);
  for (let i = 1; i <= daysInMonth; i++) grid.push(new Date(year, month, i));
  return grid;
}

function isSameDate(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isInRange(date, start, end) {
  if (!date || !start || !end) return false;
  const d = date.getTime();
  const s = Math.min(start.getTime(), end.getTime());
  const e = Math.max(start.getTime(), end.getTime());
  return d > s && d < e;
}

function formatDateShort(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export default function DateRangeSelector({ value, onChange, placeholder = 'Select dates' }) {
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [clickCount, setClickCount] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const popoverRef = useRef(null);

  // Sync from value prop
  useEffect(() => {
    if (value?.start) {
      const [y, m, d] = value.start.split('-').map(Number);
      setStartDate(new Date(y, m - 1, d));
      setCurrentMonth({ year: y, month: m - 1 });
    }
    if (value?.end) {
      const [y, m, d] = value.end.split('-').map(Number);
      setEndDate(new Date(y, m - 1, d));
    }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleDateClick = (date) => {
    if (clickCount === 0) {
      setStartDate(date);
      setEndDate(date);
      setClickCount(1);
    } else {
      if (date < startDate) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
      setClickCount(0);
    }
  };

  const handleApply = () => {
    const fmt = (d) => {
      if (!d) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const s = startDate || new Date();
    const e = endDate || s;
    onChange({ start: fmt(s), end: fmt(e) });
    setOpen(false);
  };

  const navigate = (delta) => {
    setCurrentMonth(prev => {
      let m = prev.month + delta;
      let y = prev.year;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      return { year: y, month: m };
    });
  };

  const grid = getMonthGrid(currentMonth.year, currentMonth.month);
  const monthLabel = new Date(currentMonth.year, currentMonth.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Display value
  const displayText = value?.start
    ? value.start === value.end
      ? formatDateShort(startDate)
      : `${formatDateShort(startDate)} \u2192 ${formatDateShort(endDate)}`
    : placeholder;

  return (
    <div className="relative" ref={popoverRef}>
      {/* Trigger — uses BIDIT input styling */}
      <button
        type="button"
        onClick={() => { setOpen(!open); setClickCount(0); }}
        className="input w-full flex items-center gap-2 text-left"
      >
        <CalendarDaysIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
        <span className="flex-1 truncate" style={{ color: value?.start ? 'var(--color-text-primary)' : 'var(--color-text-disabled)' }}>
          {displayText}
        </span>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
      </button>

      {/* Popover — single month */}
      {open && (
        <div className="absolute z-30 top-full mt-2 left-0 right-0 bg-white border rounded-lg p-4" style={{ borderColor: 'var(--color-border)' }}>
          {/* Month header with nav */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate(-1)}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Previous month"
            >
              <ChevronLeftIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
            </button>
            <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{monthLabel}</span>
            <button
              onClick={() => navigate(1)}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Next month"
            >
              <ChevronRightIcon className="h-4 w-4" style={{ color: 'var(--color-text-muted)' }} />
            </button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map(d => (
              <div key={d} className="text-center text-[10px] font-medium py-1" style={{ color: 'var(--color-text-subtle)' }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-0.5">
            {grid.map((date, idx) => {
              if (!date) return <div key={`empty-${idx}`} className="h-8" />;
              const isStart = isSameDate(date, startDate);
              const isEnd = isSameDate(date, endDate);
              const isEndpoint = isStart || isEnd;
              const inRange = isInRange(date, startDate, endDate);
              const isToday = isSameDate(date, new Date());

              let style = {};
              let cls = 'h-8 w-full text-xs font-medium rounded transition-colors ';
              if (isEndpoint) {
                style = { backgroundColor: 'var(--color-action-primary)', color: 'white' };
              } else if (inRange) {
                style = { backgroundColor: 'var(--red-50)', color: 'var(--color-text-primary)' };
              } else if (isToday) {
                style = { color: 'var(--color-action-primary)' };
                cls += 'font-bold hover:bg-gray-100';
              } else {
                style = { color: 'var(--color-text-default)' };
                cls += 'hover:bg-gray-100';
              }

              return (
                <button key={date.getTime()} onClick={() => handleDateClick(date)} className={cls} style={style}>
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--color-border)' }}>
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {startDate && endDate
                ? isSameDate(startDate, endDate)
                  ? formatDateShort(startDate)
                  : `${formatDateShort(startDate)} \u2192 ${formatDateShort(endDate)}`
                : 'Click a date to start'}
            </span>
            <button
              onClick={handleApply}
              disabled={!startDate}
              className="btn btn-primary text-xs px-4 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
